/**
 * Core implementation of Morph Fast Apply
 *
 * This module provides file-based operations that require Node.js.
 * For edge-compatible code-in/code-out operations, use applyEdit from ./apply.ts
 */

import { join, resolve, relative } from 'path';
import type {
  EditFileInput,
  EditFileResult,
  EditFileConfig,
} from './types.js';
import { logger } from '../../logger.js';
import { MorphAPIClient } from '../../core/client.js';
import { APIResource } from '../../core/resource.js';
import {
  FastApplyAnchorIntegrityError,
  FastApplyContextLengthError,
} from './apply.js';

// Re-export edge-compatible functions
export { applyEdit, generateUdiff, countChanges, callMorphAPI } from './apply.js';
export { FastApplyAnchorIntegrityError, FastApplyContextLengthError };

const DEFAULT_CONFIG: Required<Omit<EditFileConfig, 'morphApiKey' | 'systemPrompt' | 'retryConfig' | 'description' | 'large'>> = {
  morphApiUrl: 'https://api.morphllm.com',
  baseDir: process.cwd(),
  generateUdiff: true,
  autoWrite: true,
  timeout: 600000,
  anchorCompaction: true,
  anchorCompactionThreshold: 128 * 1024,
  debug: false,
};

/**
 * FastApply client for programmatic file editing
 * Note: This client requires Node.js for file operations
 *
 * @deprecated Prefer the unified `MorphClient` (`new MorphClient({ apiKey }).fastApply`).
 * Standalone clients remain only for backwards compatibility and may be removed in a future
 * major version — do not use them in new code.
 */
export class FastApplyClient extends APIResource {
  private config: EditFileConfig;

  constructor(clientOrConfig: MorphAPIClient | { apiKey?: string; debug?: boolean; timeout?: number; retryConfig?: any } = {}) {
    const isClient = clientOrConfig instanceof MorphAPIClient;
    super(
      isClient
        ? clientOrConfig
        : new MorphAPIClient({
            apiKey: clientOrConfig.apiKey,
            timeout: clientOrConfig.timeout ?? DEFAULT_CONFIG.timeout,
            retryConfig: clientOrConfig.retryConfig,
            debug: clientOrConfig.debug,
          }),
    );
    // FastApply talks to /v1/chat/completions through the `openai` package, but
    // sources its credentials and host from the shared transport.
    this.config = {
      morphApiKey: this._client.resolveApiKey(),
      morphApiUrl: this._client.baseURL,
      debug: this._client.debug,
      timeout: (isClient ? undefined : clientOrConfig.timeout) ?? DEFAULT_CONFIG.timeout,
      retryConfig: this._client.retryConfig,
      generateUdiff: DEFAULT_CONFIG.generateUdiff,
      autoWrite: DEFAULT_CONFIG.autoWrite,
    };
  }

  /**
   * Execute a file edit operation
   *
   * @param input - Edit parameters including filepath, instructions, and code_edit
   * @param overrides - Optional config overrides for this operation
   * @returns Edit result with success status and changes
   */
  async execute(input: EditFileInput, overrides?: Partial<EditFileConfig>): Promise<EditFileResult> {
    return executeEditFile(input, { ...this.config, ...overrides });
  }

  /**
   * Apply an edit to code directly without file I/O
   *
   * Useful for sandbox environments or when you manage your own file system.
   * Compatible with the earlier OpenAI client API contract.
   *
   * @param input - Code and edit parameters
   * @param overrides - Optional config overrides for this operation
   * @returns Result with merged code
   *
   * @example
   * ```typescript
   * const result = await client.applyEdit({
   *   originalCode: 'function hello() { return "world"; }',
   *   codeEdit: 'function hello() { return "universe"; }',
   *   instructions: 'Change return value'
   * });
   * console.log(result.mergedCode);
   * ```
   */
  async applyEdit(input: import('./types.js').ApplyEditInput, overrides?: Partial<import('./types.js').ApplyEditConfig>): Promise<import('./types.js').ApplyEditResult> {
    const { applyEdit } = await import('./apply.js');
    return applyEdit(input, { ...this.config, ...overrides });
  }
}

/**
 * Execute a file edit using Morph Fast Apply
 * Note: This function requires Node.js for file I/O
 */
export async function executeEditFile(
  input: EditFileInput,
  config: EditFileConfig = {}
): Promise<EditFileResult> {
  const baseDir = config.baseDir || DEFAULT_CONFIG.baseDir;
  const fullPath = resolve(join(baseDir, input.target_filepath));

  logger.debug('FastApply', 'execute_start', { target_filepath: input.target_filepath, baseDir, autoWrite: config.autoWrite !== false });

  // Security: ensure file is within baseDir
  const relativePath = relative(baseDir, fullPath);
  if (relativePath.startsWith('..') || fullPath === baseDir) {
    logger.warn('FastApply', 'security_check_failed', { target_filepath: input.target_filepath, relative: relativePath });
    return {
      success: false,
      filepath: input.target_filepath,
      changes: { linesAdded: 0, linesRemoved: 0, linesModified: 0 },
      error: `Invalid filepath: '${input.target_filepath}' is outside baseDir`,
    };
  }

  logger.debug('FastApply', 'security_check', { resolved: fullPath, relative: relativePath });

  try {
    // Dynamic import to avoid loading Node.js fs module at module evaluation time
    const { readFile, writeFile } = await import('fs/promises');
    const { callMorphAPI, generateUdiff, countChanges } = await import('./apply.js');

    // Handle both existing and non-existent files
    let originalCode = '';
    let fileExists = true;
    try {
      originalCode = await readFile(fullPath, 'utf-8');
      logger.debug('FastApply', 'file_read', { path: fullPath, exists: true, size_bytes: originalCode.length });
    } catch (error: any) {
      if (error.code !== 'ENOENT') {
        throw error; // Re-throw if it's not a "file not found" error
      }
      fileExists = false;
      logger.debug('FastApply', 'file_read', { path: fullPath, exists: false });
    }

    const instruction = input.instruction ?? input.instructions ?? '';
    logger.debug('FastApply', 'api_call', { instruction_len: instruction.length, code_edit_len: input.code_edit.length, original_len: originalCode.length });

    const { content: mergedCode, completionId } = await callMorphAPI(originalCode, input.code_edit, instruction, input.target_filepath, config);

    logger.debug('FastApply', 'api_response', { completion_id: completionId, merged_len: mergedCode.length });

    const udiff = config.generateUdiff !== false ? generateUdiff(originalCode, mergedCode, input.target_filepath) : undefined;

    if (config.autoWrite !== false) {
      await writeFile(fullPath, mergedCode, 'utf-8');
      logger.debug('FastApply', 'file_write', { path: fullPath, size_bytes: mergedCode.length });
    }

    const changes = countChanges(originalCode, mergedCode);
    logger.debug('FastApply', 'changes', { added: changes.linesAdded, removed: changes.linesRemoved, modified: changes.linesModified });

    return {
      success: true,
      filepath: input.target_filepath,
      udiff,
      changes,
      completionId,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    const structuredError = error instanceof FastApplyContextLengthError
      || error instanceof FastApplyAnchorIntegrityError
      ? error
      : undefined;
    logger.error('FastApply', 'execute_error', { error: errorMessage, target_filepath: input.target_filepath, stack: error instanceof Error ? error.stack : undefined });

    return {
      success: false,
      filepath: input.target_filepath,
      changes: { linesAdded: 0, linesRemoved: 0, linesModified: 0 },
      error: errorMessage,
      errorCode: structuredError?.code,
      completionId: structuredError?.completionId,
    };
  }
}
